import { useState } from 'react';
import { Menu, X, ChevronDown, Search } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Design Philosophy: Minimalismo Corporativo
 * - Navegação limpa e direta
 * - Logo com presença forte
 * - CTA bem destacado
 * - Espaço em branco generoso
 */

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isBlogOpen, setIsBlogOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const services = [
    { label: 'SEO para Advogados', href: '/servicos/seo-para-advogados' },
    { label: 'Tráfego Pago para Advogados', href: '/servicos/trafego-pago-para-advogados' },
    { label: 'Sites para Advogados', href: '/servicos/sites-para-advogados' },
    { label: 'Landing Pages para Advogados', href: '/servicos/landing-pages-para-advogados' },
    { label: 'Marketing de Conteúdo para Advogados', href: '/servicos/marketing-de-conteudo-para-advogados' },
    { label: 'CRM para Advogados', href: '/servicos/crm-para-advogados' },
  ];

  const blogCategories = [
    { label: 'SEO', href: '/blog/seo' },
    { label: 'Tráfego Pago', href: '/blog/trafego-pago' },
    { label: 'Marketing de Conteúdo', href: '/blog/marketing-conteudo' },
    { label: 'Inteligência Artificial', href: '/blog/ia' },
    { label: 'Landing Pages', href: '/blog/landing-pages' },
    { label: 'Redes Sociais', href: '/blog/redes-sociais' },
    { label: 'Sites', href: '/blog/sites' },
  ];

  const navItems = [
    { label: 'Início', href: '/' },
    { label: 'Blog', href: '/blog' },
    { label: 'FAQ', href: '/faq' },
    { label: 'Política de Privacidade', href: '/politica-de-privacidade' },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200">
      <div className="container mx-auto px-4 py-3 sm:py-4 md:py-6" style={{ minHeight: '80px' }}>
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src="/manus-storage/logo-go-marketing-digital-header-282x99_a770875d.webp"
              alt="GO Marketing Digital | Agência de Marketing Jurídico"
              className="header-logo cursor-pointer hover:opacity-80 transition-opacity"
              width="282"
              height="99"
              loading="eager"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 lg:gap-8">
            {/* Início Link */}
            <a
              href="/"
              className="text-gray-700 hover:text-[#1f6f9f] transition-colors duration-300 font-medium text-base md:text-lg lg:text-xl"
            >
              Início
            </a>

            {/* Services Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <a href="/servicos" className="flex items-center gap-1 text-gray-700 hover:text-[#1f6f9f] transition-colors duration-300 font-medium text-base md:text-lg lg:text-xl">
                Serviços
                <ChevronDown size={18} className={`transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
              </a>
              
              {/* Services Dropdown Menu */}
              <div className={`absolute left-0 mt-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 transition-all duration-200 ${isServicesOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                {services.map((service) => (
                  <a
                    key={service.label}
                    href={service.href}
                    className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-[#1f6f9f] transition-colors duration-200 text-sm md:text-base border-b border-gray-100 last:border-b-0"
                  >
                    {service.label}
                  </a>
                ))}
              </div>
            </div>

            {/* Blog Dropdown */}
            <div
              className="relative group"
              onMouseEnter={() => setIsBlogOpen(true)}
              onMouseLeave={() => setIsBlogOpen(false)}
            >
              <a href="/blog" className="flex items-center gap-1 text-gray-700 hover:text-[#1f6f9f] transition-colors duration-300 font-medium text-base md:text-lg lg:text-xl">
                Blog
                <ChevronDown size={18} className={`transition-transform duration-300 ${isBlogOpen ? 'rotate-180' : ''}`} />
              </a>
              
              {/* Blog Dropdown Menu */}
              <div className={`absolute left-0 mt-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 transition-all duration-200 ${isBlogOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
                {blogCategories.map((category) => (
                  <a
                    key={category.label}
                    href={category.href}
                    className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-[#1f6f9f] transition-colors duration-200 text-sm md:text-base border-b border-gray-100 last:border-b-0"
                  >
                    {category.label}
                  </a>
                ))}
              </div>
            </div>



            {/* FAQ e Política de Privacidade */}
            {navItems.slice(2).map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-gray-700 hover:text-[#1f6f9f] transition-colors duration-300 font-medium text-base md:text-lg lg:text-xl"
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Desktop CTA Buttons */}
          <div className="hidden md:flex items-center gap-3 lg:gap-4">
            {/* Search Button */}
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2.5 lg:p-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all duration-300 flex items-center justify-center"
              aria-label="Buscar"
              title="Buscar no site"
            >
              <Search size={20} />
            </button>
            
            {/* Search Modal */}
            {isSearchOpen && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-start justify-center pt-20" onClick={() => setIsSearchOpen(false)}>
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-4" onClick={(e) => e.stopPropagation()}>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Search size={24} className="text-[#1f6f9f]" />
                      <input
                        type="text"
                        placeholder="Buscar artigos, serviços..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                        className="flex-1 text-lg border-b-2 border-gray-300 focus:border-[#1f6f9f] outline-none py-2 transition-colors"
                      />
                      <button
                        onClick={() => setIsSearchOpen(false)}
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <X size={24} />
                      </button>
                    </div>
                    {searchQuery && (
                      <div className="mt-4 text-gray-600">
                        <p className="text-sm">Buscando por: <strong>{searchQuery}</strong></p>
                        <p className="text-sm mt-2">Redirecionando para resultados...</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            <a
              href="/#formulario-contato"
              className="px-5 lg:px-7 py-2.5 lg:py-3 bg-[#1f6f9f] text-white rounded-lg font-semibold hover:bg-[#0d47a1] transition-all duration-300 hover:shadow-lg text-sm md:text-base lg:text-base"
            >
              Contato
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <X size={20} className="text-gray-700" />
            ) : (
              <Menu size={20} className="text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4 animate-in fade-in duration-300" style={{ containIntrinsicSize: 'auto 200px' }}>
            <div className="flex flex-col gap-4">
              {/* Mobile Services Link */}
              <a
                href="/servicos"
                className="text-gray-700 hover:text-[#1f6f9f] transition-colors font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Serviços
              </a>

              {/* Mobile Blog Dropdown */}
              <div>
                <button
                  onClick={() => setIsBlogOpen(!isBlogOpen)}
                  className="w-full text-left text-gray-700 hover:text-[#1f6f9f] transition-colors font-medium flex items-center justify-between"
                >
                  Blog
                  <ChevronDown size={18} className={`transition-transform duration-300 ${isBlogOpen ? 'rotate-180' : ''}`} />
                </button>
                {isBlogOpen && (
                  <div className="mt-2 pl-4 border-l-2 border-[#1f6f9f] space-y-2">
                    {blogCategories.map((category) => (
                      <a
                        key={category.label}
                        href={category.href}
                        className="block text-gray-700 hover:text-[#1f6f9f] transition-colors text-sm"
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsBlogOpen(false);
                        }}
                      >
                        {category.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile Services Dropdown - Hidden */}
              <div className="hidden">
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="w-full text-left text-gray-700 hover:text-[#1f6f9f] transition-colors font-medium flex items-center justify-between"
                >
                  Serviços
                  <ChevronDown size={18} className={`transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
                </button>
                {isServicesOpen && (
                  <div className="mt-2 pl-4 border-l-2 border-[#1f6f9f] space-y-2">
                    {services.map((service) => (
                      <a
                        key={service.label}
                        href={service.href}
                        className="block text-gray-700 hover:text-[#1f6f9f] transition-colors text-sm"
                        onClick={() => {
                          setIsMenuOpen(false);
                          setIsServicesOpen(false);
                        }}
                      >
                        {service.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {navItems.slice(2).map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  className="text-gray-700 hover:text-[#1f6f9f] transition-colors font-medium"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
              <a
                href="/#formulario-contato"
                className="px-6 py-2.5 bg-[#1f6f9f] text-white rounded-lg font-semibold hover:bg-[#0d47a1] transition-all duration-300 text-center"
                onClick={() => setIsMenuOpen(false)}
              >
                Contato
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
}
